import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { database, ref, get } from "../firebase";
import { Link, useNavigate } from "react-router-dom";
import { checkAndSetStats, scrollToElement } from "../helpers/helpers";
import twoLogos from "../images/twoLogos.svg";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import SEO from "../components/SEO";

// Hook for scroll-triggered animations
const useScrollAnimation = () => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, []);

    return [ref, isVisible];
};

// Animated section wrapper
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
    const [ref, isVisible] = useScrollAnimation();

    return (
        <div
            ref={ref}
            className={`scroll-animate ${isVisible ? "is-visible" : ""} ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

// Button link for collections
const ButtonLink = ({ item, isBottomBtn }) => {
    return (
        <Link
            to={item.href}
            className={`${
                isBottomBtn
                    ? "lg:hidden block w-auto mb-4 mx-4 bg-brand-gold text-white py-3"
                    : "hidden lg:block w-full mt-8 bg-brand-gold hover:bg-brand-gold-hover p-4 text-white transition-colors"
            } rounded-sm uppercase text-center font-medium tracking-wide`}
        >
            Перейти до колекції
        </Link>
    );
};

// Collection row component
const CollectionRow = ({ item, index }) => {
    const navigate = useNavigate();
    const [ref, isVisible] = useScrollAnimation();

    return (
        <div
            ref={ref}
            className={`scroll-animate ${isVisible ? "is-visible" : ""}`}
            style={{ transitionDelay: `${index * 100}ms` }}
        >
            <div
                className="cursor-pointer group"
                onClick={() => navigate(item.href)}
            >
                <div className="flex w-full flex-row justify-between border-t border-neutral-200">
                    <div className="w-1/4 lg:w-1/4 img-zoom">
                        <img
                            className="w-full h-auto object-cover max-h-[250px] lg:max-h-[700px] transition-transform duration-500 group-hover:scale-[1.02]"
                            src={item.leftImage}
                            alt={item.name}
                        />
                    </div>

                    <div
                        id={index === 0 ? "firstCollection" : undefined}
                        className="w-2/4 lg:w-2/4 flex flex-col justify-center p-2 lg:px-12 items-center"
                    >
                        <img
                            src={item.image}
                            alt={item.name}
                            className="max-w-[80px] lg:max-w-[280px] transition-transform duration-300 group-hover:scale-105"
                        />

                        <h3 className="text-sm lg:text-2xl text-center mt-4 lg:mt-8 font-light tracking-wide text-neutral-800">
                            Колекція {item.name}
                        </h3>

                        <ButtonLink item={item} />
                    </div>

                    <div className="w-1/4 lg:w-1/4 img-zoom">
                        <img
                            className="w-full h-auto object-cover max-h-[250px] lg:max-h-[700px] transition-transform duration-500 group-hover:scale-[1.02]"
                            src={item.rightImage}
                            alt={item.name}
                        />
                    </div>
                </div>
                <ButtonLink item={item} isBottomBtn={true} />
            </div>
        </div>
    );
};

function Home() {
    const doesStatsSent = useRef(false);
    const [products, setProducts] = useState([]);

    useLayoutEffect(() => {
        document.body.style.backgroundColor = "#FFFFFF";
        const dbRef = ref(database, "/landing");

        get(dbRef)
            .then((snapshot) => {
                if (snapshot.exists()) {
                    const { items } = snapshot.val();
                    setProducts(items);
                } else {
                    console.log("No data available");
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    useEffect(() => {
        if (!doesStatsSent.current) {
            checkAndSetStats("homePage");
            doesStatsSent.current = true;
        }
    }, []);

    const activeProducts = products.filter((item) => item.isActive);

    return (
        <div className="bg-white min-h-screen">
            <SEO
                title="Головна"
                description="Елегантні ювелірні вироби від бренду «Цацки» у Харкові. Колекції Харківської ювелірної фабрики — унікальні каблучки, сережки, підвіски та браслети ручної роботи."
                url="/"
            />
            <Navigation variant="light" />

            <main role="main">
                {/* Hero Section */}
                <header className="relative h-[85vh] lg:h-screen flex items-center home-head-container bg-cover bg-right md:bg-center">
                    <div className="absolute inset-0 hero-gradient" />
                    <div className="relative z-10 px-6 lg:px-16 max-w-4xl">
                        <h1 className="opacity-0 animate-fade-in-up text-white text-5xl lg:text-7xl font-bold tracking-tight mb-4">
                            КОЛЕКЦІЇ
                        </h1>
                        <p className="opacity-0 animate-fade-in-up animate-delay-100 text-white/90 text-2xl lg:text-4xl font-light leading-tight mb-8">
                            Харківської ювелірної фабрики
                        </p>
                        <div className="mb-4 opacity-0 animate-fade-in-up animate-delay-200 flex flex-col sm:flex-row gap-4">
                            <Link
                                to={"#"}
                                onClick={() => scrollToElement("firstCollection")}
                                className="w-full btn-animate bg-brand-gold hover:bg-brand-gold-hover text-white px-8 py-4 rounded-sm text-center font-medium tracking-wide uppercase transition-colors"
                            >
                                Перейти до колекцій
                            </Link>
                        </div>

                        <div className="opacity-0 animate-fade-in-up animate-delay-200 flex flex-col sm:flex-row gap-4">
                            <Link
                                to="/catalog"
                                className="w-full btn-animate bg-brand-gold hover:bg-brand-gold-hover text-white px-8 py-4 rounded-sm text-center font-medium tracking-wide uppercase transition-colors"
                            >
                                Перейти до каталогу
                            </Link>
                        </div>
                    </div>
                </header>

                {/* Logo Divider */}
                <AnimatedSection className="py-12 lg:py-16 flex justify-center">
                    <img
                        className="w-[120px] lg:w-[160px] opacity-80"
                        src={twoLogos}
                        alt="ЦАЦКИ & ХЮФ"
                    />
                </AnimatedSection>

                {/* About Section */}
                <section className="px-6 lg:px-16 py-12 lg:py-20 max-w-5xl mx-auto" aria-label="About collections">
                    <AnimatedSection>
                        <h2 className="text-4xl lg:text-5xl font-light text-neutral-800 mb-8 tracking-tight">
                            Про колекції
                        </h2>
                    </AnimatedSection>
                    <AnimatedSection delay={100}>
                        <div className="text-lg lg:text-xl text-neutral-600 leading-relaxed space-y-4 mb-10">
                            <p>
                                Колекції Харківської ювелірної фабрики — це поєднання
                                майстерності, глибоких символів і сучасного стилю. У
                                кожній моделі — частинка нашого міста: його сила,
                                витримка і незламна краса.
                            </p>
                            <p>
                                Ми створюємо прикраси для тих, хто цінує не тільки
                                форму, а й сенс. Від стриманої класики до виразного
                                дизайну — у нас є колекція, що підкреслить саме вашу
                                історію.
                            </p>
                        </div>
                    </AnimatedSection>
                    <AnimatedSection delay={200}>
                        <p className="text-2xl lg:text-3xl text-brand-gold font-light text-center tracking-wide">
                            Харків. Краса. Кохання. Ти.
                        </p>
                    </AnimatedSection>
                </section>

                {/* Collections */}
                <section aria-label="Collections">
                    {activeProducts.map((item, index) => (
                        <CollectionRow key={item.href || index} item={item} index={index} />
                    ))}
                </section>

                {/* Final CTA */}
                <section className="px-6 lg:px-16 py-20 lg:py-28 bg-brand-gold-light text-neutral-800 text-center">
                    <AnimatedSection>
                        <h2 className="text-3xl lg:text-4xl font-light mb-4 tracking-tight">
                            Знайдіть свою прикрасу
                        </h2>
                    </AnimatedSection>
                    <AnimatedSection delay={100}>
                        <p className="text-lg text-neutral-600 mb-8 max-w-xl mx-auto">
                            Відвідайте наш каталог та оберіть виріб, що підкреслить вашу індивідуальність
                        </p>
                    </AnimatedSection>
                    <AnimatedSection delay={200}>
                        <Link
                            to="/catalog"
                            className="inline-block btn-animate bg-brand-gold hover:bg-brand-gold-hover text-white px-12 py-5 rounded-sm text-lg font-medium tracking-wide uppercase transition-colors"
                        >
                            Перейти до каталогу
                        </Link>
                    </AnimatedSection>
                </section>
            </main>

            <Footer containerClassNames="bg-white text-vidrodzhennya" />
        </div>
    );
}

export default Home;
